'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';

interface ProfileAvatarProps {
  profilePicture: string | null;
  userInitials: string;
  /** Cache-bust value - update when profile picture changes to force fresh load */
  cacheBust?: number;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Profile avatar with initials fallback and cache-busting.
 * Gradient background only when showing initials; neutral bg when image is present.
 */
export function ProfileAvatar({
  profilePicture,
  userInitials,
  cacheBust,
  size = 'sm',
  className = '',
}: ProfileAvatarProps) {
  const [imageError, setImageError] = useState(false);

  // Reset error state when profile picture or cache changes
  useEffect(() => {
    setImageError(false);
  }, [profilePicture, cacheBust]);

  const hasValidImage = profilePicture && profilePicture.trim() !== '' && !imageError;
  const imageUrl = hasValidImage && profilePicture
    ? `${profilePicture}${profilePicture.includes('?') ? '&' : '?'}t=${cacheBust ?? 0}`
    : '';

  const sizeClasses = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10 lg:h-9 lg:w-9';

  return (
    <div
      className={`relative flex cursor-pointer ${sizeClasses} items-center justify-center rounded-full overflow-hidden flex-shrink-0 ${hasValidImage ? 'bg-surface-3' : 'profile-avatar-gradient'} ${className}`}
    >
      {hasValidImage ? (
        <Image
          src={imageUrl}
          alt="Profile"
          fill
          className="object-cover cursor-pointer"
          unoptimized={true}
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="cs-h5 text-onAction-strong">{userInitials}</span>
      )}
    </div>
  );
}
